import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { firstBy } from 'thenby';
import { IWorklistItemDto, StatusType, UrgencyType, WorklistItemDto } from '../../api-module/control-centre-api-client';
import moment from 'moment';
import { Constants } from '../../models/constants';
import { DetailsComponent } from '../details/details.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent implements OnChanges {
    INITIAL_PAGE_SIZE = 15;

    @Input()
    worklistItems: WorklistItemDto[] | null;

    @Input()
    filtersApplied: boolean;

    @ViewChild(MatSort, { static: true }) sort: MatSort;

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

    @Output()
    openRow = new EventEmitter<IWorklistItemDto>();

    @Output()
    visualRowSizeChanged = new EventEmitter<number>();

    @Input()
    selectedItem: IWorklistItemDto;

    displayedColumns: string[] = [
        'urgency',
        'status',
        'mrn',
        'patientName',
        'studyDateTime',
        'imageCount',
        'studyDescription',
        'modality',
        'referringDoctor',
        'info'
    ];

    dataSource = new MatTableDataSource<WorklistItemDto>();

    get showLoadMore(): boolean {
        return this.worklistItems?.length > this.paginator?.pageSize;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.worklistItems) {
            // sort control on UI
            this.dataSource.sort = this.sort;
            // custom sort logic
            this.dataSource.sortData = (data: WorklistItemDto[], sort: MatSort) => {
                switch (sort.direction) {
                    case 'asc':
                        return this.sortWorkListItems(data, sort.active, true);

                    case 'desc':
                        return this.sortWorkListItems(data, sort.active, false);

                    default:
                        // Clear sorting scenario
                        return this.sortWorkListItems(data); // Default sorting
                }
            };
            // pagination
            this.dataSource.paginator = this.paginator;

            if (this.worklistItems === undefined || this.worklistItems === null) {
                this.dataSource.data = [];
                this.paginator.length = 0;
                this.emitVisualRowSize(0, 0);
            } else {
                this.dataSource.data = this.worklistItems;
                this.paginator.length = this.worklistItems.length;
                this.emitVisualRowSize(this.worklistItems.length, this.paginator.pageSize ?? this.INITIAL_PAGE_SIZE);
            }
        }
    }

    sortWorkListItems(rows: WorklistItemDto[], fieldName = 'urgency', isAsc = false): WorklistItemDto[] {
        return rows.sort(
            firstBy((a: WorklistItemDto, b: WorklistItemDto) => {
                if (fieldName === 'urgency') {
                    return this.compareUrgency(a.urgency, b.urgency, isAsc);
                } else if (fieldName === 'status') {
                    return this.compareStatus(a.status, b.status, isAsc);
                } else {
                    return this.commonCompare(a[fieldName], b[fieldName], isAsc);
                }
            })
                .thenBy((a: WorklistItemDto, b: WorklistItemDto) => {
                    // studyDateTime most recent first
                    if (a.studyDateTime && !b.studyDateTime) {
                        // StudyDateTime exists on top
                        return 1;
                    } else if (!a.studyDateTime && b.studyDateTime) {
                        return -1;
                    } else if (!a.studyDateTime && !b.studyDateTime) {
                        return 0;
                    } else {
                        const aMilliseconds = moment(a.studyDateTime).toDate().valueOf();
                        const bMilliseconds = moment(b.studyDateTime).toDate().valueOf();
                        return aMilliseconds > bMilliseconds ? -1 : bMilliseconds > aMilliseconds ? 1 : 0;
                    }
                })
                .thenBy((a: WorklistItemDto, b: WorklistItemDto) => {
                    // createdAt most recent first
                    if (a.createdAt && !b.createdAt) {
                        return 1;
                    } else if (!a.createdAt && b.createdAt) {
                        return -1;
                    } else if (!a.createdAt && !b.createdAt) {
                        return 0;
                    } else {
                        const aMilliseconds = moment(a.createdAt).toDate().valueOf();
                        const bMilliseconds = moment(b.createdAt).toDate().valueOf();
                        return aMilliseconds > bMilliseconds ? -1 : bMilliseconds > aMilliseconds ? 1 : 0;
                    }
                })
        );
    }

    compareUrgency(a: string, b: string, isAsc: boolean): number {
        if (a && !b) {
            return 1;
        } else if (!a && b) {
            return -1;
        } else if (!a && !b) {
            return 0;
        }

        const urgencyPriorities = {
            TraumaAndStroke: 5,
            Stroke: 4,
            Trauma: 3,
            Urgent: 2,
            Routine: 1
        };
        return (urgencyPriorities[a] - urgencyPriorities[b]) * (isAsc ? 1 : -1);
    }

    compareStatus(a: StatusType | null, b: StatusType | null, isAsc: boolean): number {
        const descA = this.getStatusDescription(a);
        const descB = this.getStatusDescription(b);
        return this.commonCompare(descA, descB, isAsc);
    }

    commonCompare(valueA: any, valueB: any, isAsc: boolean): number {
        // If there are data in the column that can be converted to a number,
        // it must be ensured that the rest of the data
        // is of the same type so as not to order incorrectly.
        const valueAType = typeof valueA;
        const valueBType = typeof valueB;

        if (valueAType !== valueBType) {
            if (valueAType === 'number') {
                valueA += '';
            }
            if (valueBType === 'number') {
                valueB += '';
            }
        }

        // If both valueA and valueB exist (truthy), then compare the two. Otherwise, check if
        // one value exists while the other doesn't. In this case, existing value should come last.
        // This avoids inconsistent results when comparing values to undefined/null.
        // If neither value exists, return 0 (equal).
        let comparatorResult = 0;
        if (valueA != null && valueB != null) {
            // Check if one value is greater than the other; if equal, comparatorResult should remain 0.
            if (valueA > valueB) {
                comparatorResult = 1;
            } else if (valueA < valueB) {
                comparatorResult = -1;
            }
        } else if (valueA != null) {
            comparatorResult = 1;
        } else if (valueB != null) {
            comparatorResult = -1;
        }

        return comparatorResult * (isAsc ? 1 : -1);
    }

    getClass(row: WorklistItemDto) {
        const cssClass = { 'selected-row': this.selectedItem?.id === row.id };
        switch (row.urgency) {
            case UrgencyType.Routine:
                cssClass['urgency-routine'] = true;
                break;
            case UrgencyType.Stroke:
            case UrgencyType.Trauma:
            case UrgencyType.Urgent:
            case UrgencyType.TraumaAndStroke:
            default:
                cssClass['urgency-urgent'] = true;
                break;
        }
        switch (row.status) {
            case StatusType.OnHoldMissingClinicalHistory:
            case StatusType.OnHoldMissingImages:
            case StatusType.OnHoldMissingDocumentation:
            case StatusType.OnHoldMissingPriors:
            case StatusType.OnHoldMissingPriorImages:
            case StatusType.OnHoldReviewOrderDetails:
                cssClass['on-hold'] = true;
                break;
            default:
                // do nothing
                break;
        }
        return cssClass;
    }

    loadMore(): void {
        this.paginator.pageSize = this.paginator.pageSize + 10;
        this.dataSource._updateChangeSubscription();
        this.emitVisualRowSize(this.worklistItems.length, this.paginator.pageSize);
    }

    emitVisualRowSize(rowSize: number, pageSize: number) {
        this.visualRowSizeChanged.emit(Math.min(rowSize, pageSize));
    }

    formatStudyDateTime(studyDateTime: string): string {
        if (studyDateTime) {
            return moment(studyDateTime).format('DD/MM/YYYY HH:mm:ss');
        } else {
            return null;
        }
    }

    onOpenRow(item: WorklistItemDto) {
        this.openRow.emit(item);
    }

    adjustImageCount(imageCount: number): number {
        return Math.max(imageCount || 0, 0);
    }

    getUrgencyDescription(urgency: UrgencyType) {
        return Constants.UrgencyDescriptions.get(urgency);
    }

    getStatusDescription(status: StatusType) {
        return Constants.StatusDescriptions.get(status);
    }

    /**
     * This method determines if the warning (triangle) icon should be showing
     * @returns boolean
     */
    hasMissingFields(item: WorklistItemDto) {
        if (DetailsComponent.isWorklistItemEditable(item) && !DetailsComponent.isWorklistItemRoutine(item)) {
            if (!item.noOfImagesExpected || item?.noOfImagesExpected === 0) {
                return true;
            }
            if (!item.referringDoctor) {
                return true;
            }
            if (!item.referringDoctorPhone) {
                return true;
            }
            if (!item.patientLocation) {
                return true;
            }
            if (item.traumaProtocol === null || item.strokeProtocolType === null) {
                return true;
            }
        }

        return false;
    }

    getItemCount(): string {
        if (!this.paginator || !this.worklistItems) {
            return '';
        } else {
            const totalItems = this.worklistItems.length;
            const displayedItems = Math.min(this.paginator.pageSize, totalItems);
            return 'Showing ' + displayedItems + '/' + totalItems;
        }
    }
}
